import {useActionData, useLocation} from '@remix-run/react';
import {useForm, useInputControl} from '@conform-to/react';
import {parseWithZod} from '@conform-to/zod';
import {action, optInSchema} from '~/routes/promotions.$promotionId.$partnerSlug/route';
import {Box, Flex, Grid, VStack} from '@styled-system/jsx';
import {h2, link, small} from '@styled-system/recipes';
import {css, cx} from '@styled-system/css';
import {Checkbox} from '~/components/ui/Checkbox';
import {Label} from '~/components/ui/Label';
import {RadioCard} from '~/components/ui/RadioCard';
import ProductOption from '~/routes/promotions.$promotionId.$partnerSlug/ProductOption';
import {Button} from '~/components/ui/Button';
import {Decimal} from '@prisma/client/runtime/binary';
import {useHydrated} from 'remix-utils/use-hydrated';
import {useEffect} from 'react';
import PandaFormInput from '~/components/forms/PandaFormInput';

type OptInFormProps = {
  partnerName?: string
  products: {
    productId: string
    title: string
    imageUrl: string
    splashImage: string
    price: Decimal
    shippingPrice: Decimal
  }[]
  upsells: {
    page: string
  }[]
  customer?: {
    firstName: string
    lastName: string
    emailAddress: string
    phoneNumber: string | null
  }
  tags: string[]
  hasFilteredProducts: boolean
  setProductImage?: (imageUrl: string | undefined) => void
}

const formatPhone = (input: string | undefined) => {
  const digits = input?.replace(/^\(\+1|[^0-9]/g, '') ?? '';
  const padded = digits.padEnd(10, '_');
  return `(+1) ${padded.slice(0, 3)}-${padded.slice(3, 6)}-${padded.slice(6)}`;
}

const OptInForm = (props: OptInFormProps) => {
  const {products, partnerName, tags, upsells, customer, hasFilteredProducts, setProductImage} = props;
  const location = useLocation();
  const isHydrated = useHydrated();

  const getReferrerParamsIfSameDomain = () => {
    if (!isHydrated) {
      return;
    }

    const referrer = document.referrer;

    if (!referrer) {
      return;
    }

    try {
      const url = new URL(referrer);

      if (url.host !== document.location.host) {
        return;
      }

      return Object.fromEntries(
        url.searchParams
      );
    } catch (e) {
      return;
    }
  }

  const lastResult = useActionData<typeof action>();
  const [form, fields] = useForm({
    lastResult,
    defaultValue: {
      firstName: customer?.firstName,
      lastName: customer?.lastName,
      email: customer?.emailAddress,
      phone: customer?.phoneNumber,
      productId: products?.[0]?.productId,
      consent: 'on',
    },
    onValidate({formData}) {
      return parseWithZod(formData, {schema: optInSchema});
    },
    shouldValidate: 'onBlur',
    shouldRevalidate: 'onInput',
  });

  const productId = useInputControl(fields.productId);
  const consent = useInputControl(fields.consent);

  useEffect(() => {
    if (setProductImage) {
      const splashImage = products.find(p => p.productId === productId.value)?.splashImage;
      setProductImage(splashImage ?? undefined);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productId.value, setProductImage, products]);

  return <form id={form.id} method="post" onSubmit={form.onSubmit} className="opt-in-form">
    <input type="hidden" name="upsells" value={JSON.stringify(upsells)}/>
    <input type="hidden" name="tags" value={JSON.stringify(tags)}/>
    <input
      type="hidden"
      name="params"
      value={JSON.stringify(isHydrated ? {
        ...(getReferrerParamsIfSameDomain() ?? {}),
        ...Object.fromEntries(new URLSearchParams(document.location.search)),
      } : {})}
    />
    <input
      type="hidden"
      name="referrer"
      value={isHydrated ? document.referrer : ''}
    />
    <input type="hidden" name="landingPage" value={isHydrated ? document.location.pathname : ''}/>
    <VStack bgColor="blue.50" borderRadius="lg" p={4} alignItems="stretch">
      <h2 className={cx(h2(), css({textAlign: 'center'}))}>
        1. Get your automatic chance to win {partnerName && <>and support <span
        className={css({color: 'primary'})}>{partnerName}</span></>}
      </h2>
      <Grid columns={{base: 1, sm:2}}>
        <PandaFormInput meta={fields.firstName} placeholder="First Name"/>
        <PandaFormInput meta={fields.lastName} placeholder="Last Name"/>
        <PandaFormInput meta={fields.email} placeholder="Email Address"/>
        <PandaFormInput
          meta={fields.phone}
          placeholder="Phone Number"
          type="tel"
          onChange={e => {
            e.target.value = formatPhone(e.target.value);
            const cursorTarget = (e.target.value.match(/\d(?!.*\d)/)?.index ?? 0) + 1;
            e.target.setSelectionRange(cursorTarget, cursorTarget);
          }}
        />
      </Grid>
      <Box px={2}>
        <Flex alignItems="flex-start" gap="2">
          <Checkbox
            id={fields.consent.id}
            name={fields.consent.name}
            defaultChecked={fields.consent.initialValue === 'on'}
            onCheckedChange={v => consent.change(v === true ? 'on' : 'off')}
          />

          <Flex flexDirection="column" gap={0}>
            <Label htmlFor={fields.consent.id}>Keep me up to date with the latest sweepstakes promotions</Label>
            <p className={cx(small(), css({textAlpha: 'black/80'}))}>
              I agree to receive text messages from Remarkabl. Message and data rates may apply. Message frequency
              varies. Text HELP for help and STOP to cancel.
              {' '}
              <a href="https://getw.in/sms-terms" target="_blank" rel="noreferrer">
                Mobile Terms of Service / Privacy Policy
              </a>
            </p>
          </Flex>
        </Flex>
      </Box>
      <h2 className={cx(h2(), css({textAlign: 'center'}))}>
        2. Select your product
      </h2>
      <VStack alignItems="stretch">
        <RadioCard
          defaultValue={fields.productId.initialValue}
          name={fields.productId.name}
          onValueChange={productId.change}
        >
          {products?.map(p => (
            <ProductOption
              key={p.productId}
              productId={p.productId}
              title={p.title}
              imgSrc={p.imageUrl}
              price={p.price.toNumber()}
              hasShipping={p.shippingPrice.greaterThan(0)}
            />
          ))}
        </RadioCard>
        {hasFilteredProducts && (customer ? (
            <p>
              Want additional product choices? <a className={link()} href="/vip-club">Join our VIP club</a> to gain access
              today!
            </p>
          ) : (
            <p>
              Want additional product choices? <a className={link()} href="/vip-club">Join our VIP
              club</a> to gain access
              today! Already a member? <a className={link()} href={`/auth/login?redirectTo=${encodeURIComponent(location.pathname)}`}
            >Login</a>
            </p>
          )
        )}
      </VStack>
      <Button alignSelf="center">
        Get my entry to win
      </Button>
    </VStack>
  </form>
};

export default OptInForm;
